import {
  Alert,
  AlertDescription,
  AlertIcon,
  ButtonGroup,
  Flex,
  Link,
  Tooltip,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import FormSwitch from "@/common/components/Form/FormSwitch";
import FormTextArea from "@/common/components/Form/FormTextArea";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureTargetingData } from "@/feature/data/useFeatureTargetingData";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import { z } from "@reflag/shared/utils/zod";

const optInFormSchema = z.object({
  optInEnabled: z.boolean(),
  optInDescription: z.string().max(5000).optional(),
});

type OptInForm = z.input<typeof optInFormSchema>;

const OPT_IN_DOCS_URL = "https://docs.reflag.com/guides/self-opt-in";

function defaultValues(feature?: FeatureDetail): OptInForm {
  return {
    optInEnabled: feature?.optInEnabled ?? false,
    optInDescription: feature?.optInDescription ?? "",
  };
}

export function SettingsOptIn() {
  const { appId, env } = useEnvironment();
  const { featureId } = useSlugParam("feature");
  const { data: feature } = useFeatureData(featureId!);
  const { data: targeting = [] } = useFeatureTargetingData(featureId!);
  const queryClient = useQueryClient();
  const updateMutation = useFeatureUpdateMutation();

  const form = useForm<OptInForm>({
    resolver: zodResolver(optInFormSchema),
    mode: "onChange",
    values: defaultValues(feature),
  });

  const optInEnabled = form.watch("optInEnabled");
  const isSecretFlag = !!feature?.secret;
  const optInDisabledBySecret = isSecretFlag && !optInEnabled;
  const optInDisabledReason =
    "It's not possible to enable End-user opt-in as long as this flag is secret. Opt-ins happen directly from the browser/client and thus require the flag to be publicly available.";
  const currentEnvironmentTargeting = targeting.find(
    (version) => version.environment.id === env.id,
  );
  const optInsAreInactive =
    !isSecretFlag &&
    feature?.optInEnabled &&
    currentEnvironmentTargeting?.targetingMode === "none";
  const willTurnTargetingModeToSome =
    !!feature &&
    !isSecretFlag &&
    !feature.optInEnabled &&
    optInEnabled &&
    currentEnvironmentTargeting?.targetingMode === "none";

  const submitHandler = useFormMutationSubmitHandler(
    form,
    updateMutation,
    (result) => {
      form.reset(defaultValues(result));
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.targeting(appId, undefined, result.id),
      });
    },
    {
      prepareVariables: (values) => ({
        id: featureId!,
        optInEnabled: values.optInEnabled,
        optInDescription: values.optInDescription,
      }),
    },
  );

  return (
    <Flex direction="column" gap={4} maxW="mediumForm">
      <Restricted alertStyle="callout" feature={feature} role="contributor">
        <FormProvider {...form}>
          <Flex as="form" direction="column" gap={4} onSubmit={submitHandler}>
            <FormSwitch
              description={
                <>
                  Let end users opt themselves or their company in. Disabling
                  stops new opt-ins and makes stored memberships inactive
                  without deleting them. Secret flags cannot use end-user
                  opt-in. See{" "}
                  <Link href={OPT_IN_DOCS_URL} isExternal>
                    Opt-in documentation
                  </Link>{" "}
                  for more information.
                </>
              }
              isDisabled={optInDisabledBySecret}
              label="Enable end-user opt-in"
              name="optInEnabled"
              switchWrapper={(switchElement) => (
                <Tooltip
                  hasArrow
                  isDisabled={!optInDisabledBySecret}
                  label={optInDisabledReason}
                  placement="top"
                  shouldWrapChildren
                >
                  {switchElement}
                </Tooltip>
              )}
            />

            {optInsAreInactive && (
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  Opt-ins are enabled, but Access is set to No one in {env.name}
                  . Opted-in users and companies won't get access until Access
                  is set to Some or Everyone.
                </AlertDescription>
              </Alert>
            )}

            {willTurnTargetingModeToSome && (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  Saving will change Access from No one to Some in {env.name} so
                  users and companies can opt in.
                </AlertDescription>
              </Alert>
            )}

            <FormTextArea
              description="Description exposed to SDKs for your own opt-in UI."
              descriptionAsTooltip
              isDisabled={!optInEnabled || !!feature?.secret}
              label="Public description"
              maxLength={5000}
              name="optInDescription"
              placeholder="The new funds transfer experience has more glitter and less bugs..."
            />

            <FormRootError />

            <ButtonGroup>
              <FormSubmit showPostSubmit />
              <FormReset />
            </ButtonGroup>
          </Flex>
        </FormProvider>
      </Restricted>
    </Flex>
  );
}
